'use client';

import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import UsageMeter from './UsageMeter';
import { Radio, RadioGroup } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';
import lightRoad from '../../assets/lightRoad.png';
import { AuthContext } from '../../context/AuthContext';

// Pricing constants
const frequencies = [
  { value: 'monthly', label: 'Monthly', priceSuffix: '/month' },
  { value: 'annually', label: 'Annually', priceSuffix: '/year' },
];

const tiers = [
  {
    name: 'Starter',
    id: 'tier-starter',
    href: '#',
    price: { monthly: '$210', annually: '$2,268' },
    costPerUser: { monthly: '$42', annually: '$100' },
    description: 'Essential tools for small paving teams.',
    features: ['Up to 5 team members', '3k Records per cycle', 'Access to Projects', 'Access to Integrations'],
    featured: false,
    cta: 'Switch plan',
  },
  {
    name: 'Pro',
    id: 'tier-pro',
    href: '#',
    price: { monthly: '$675', annually: '$7,290' },
    description: 'Designed for growing paving companies.',
    features: ['Up to 25 team members', '12k Records per cycle', 'Access to Projects', 'Access to Integrations'],
    featured: false,
    cta: 'Switch plan',
  },
  {
    name: 'Master',
    id: 'tier-master',
    href: '#',
    price: { monthly: '$1,800', annually: '$19,440' },
    description: 'Built for established paving firms with larger teams.',
    features: ['Up to 75 team members', '40k Records per cycle', 'Access to Projects', 'Access to Integrations'],
    featured: false,
    cta: 'Switch plan',
  },
  {
    name: 'Elite',
    id: 'tier-elite',
    href: '#',
    price: { monthly: '$2,875', annually: '$31,050' },
    description: 'Crafted for large-scale paving companies.',
    features: ['Up to 125 team members', '100k Records', 'Access to Projects', 'Access to Integrations'],
    featured: false,
    cta: 'Switch plan',
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Billing({ organization }) {
  const [customerId, setCustomerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [frequency, setFrequency] = useState(frequencies[0]); 

  // Fetch Stripe customer ID from Supabase
  useEffect(() => {
    const fetchCustomerId = async () => {
      try {
        if (!organization?.id) {
          console.error("Organization ID missing");
          return;
        }
        const { data, error } = await supabase
          .from("organizations")
          .select("stripe_customer_id")
          .eq("id", organization.id)
          .single();
        if (error) {
          console.error("Error fetching customer ID:", error);
          return;
        }
        setCustomerId(data.stripe_customer_id);
      } catch (err) {
        console.error("Error retrieving customerId from Supabase:", err);
      } finally {
        setLoading(false);
      }
    };
    if (organization?.id) fetchCustomerId();
  }, [organization]);

const fetchSubscriptions = async () => {
  if (!organization?.id) return;
  try {
    const { data, error } = await supabase.functions.invoke('get-subscription-product-details', {
      body: { organizationId: organization.id },
    });

    if (error) {
      throw error;
    }

    console.log('Final formatted subscriptions:', data.subscriptions);
    setSubscriptions(data.subscriptions || []);
  } catch (error) {
    console.error('Error fetching subscription info:', error.message);
    console.error('Full error:', error);
    setSubscriptions([]);
  }
};

  useEffect(() => { fetchSubscriptions(); }, [customerId]);

  // Subscription identification
  const mainSubscription = subscriptions.find((sub) =>
  sub.status === 'active' || sub.status === 'trialing'
);

const mainItem = mainSubscription?.items[0]; // Just get the first item
  // Manage subscription via Customer Portal
const handleManageSubscription = async () => {
  try {
    const { data, error } = await supabase.functions.invoke('create-customer-portal-session', {
      body: {
        customerId,
        returnUrl: `${window.location.origin}/settings`,
      },
    });

    if (error) {
      throw error;
    }

    if (data.url) {
      window.open(data.url, '_blank');
    } else {
      alert('Failed to load subscription portal.');
    }
  } catch (error) {
    console.error('Error managing subscription:', error);
    alert('An error occurred.');
  }
};

  // Open the pricing table modal
  const openPricingTableModal = () => {
    setModalOpen(true);
  };

  return (
    <div className="max-w-3xl">
      {loading ? (
        <div className="w-full p-4 border rounded-lg shadow-sm bg-white my-2 animate-pulse">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          </h3>
          <p className="text-sm text-gray-600">
            <div className="h-4 bg-gray-200 rounded w-2/3 mt-2"></div>
          </p>
          <div className="w-full bg-gray-200 rounded-full h-4 mt-4">
            <div className="h-4 bg-gray-300 rounded-full animate-pulse" style={{ width: '50%' }}></div>
          </div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mt-4"></div>
        </div>
      ) : (
        <>
          {/* Billing Header */}
          <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
            {customerId ? (
              <>
                <div className="flex items-center space-x-3">
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full"></span>
                  <p className="text-sm font-medium text-gray-700">Active Subscription</p>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Your subscription is currently active. Click below to manage your subscription settings.
                </p>
              </>
            ) : (
              <div>
                <p className="text-sm text-red-500">No active subscription found.</p>
                <button
                  onClick={() => alert('Redirect to subscription creation page')}
                  className="mt-4 px-4 py-2 text-white text-sm font-medium rounded-md hover:brightness-90"
                  style={{ backgroundColor: '#FEDD03' }}
                >
                  Subscribe Now
                </button>
              </div>
            )}
          </div>

          {/* Usage Meter */}
          {organization?.id && customerId ? (
            <UsageMeter organizationId={organization.id} stripeCustomerId={customerId} />
          ) : (
            <p className="text-sm text-red-500 mt-4">Unable to load usage meter due to missing data.</p>
          )}

          {/* Main Subscription Section */}
          {mainSubscription && mainItem ? (
            <div className="flex flex-col md:flex-row bg-white border rounded-lg shadow-sm p-6 gap-6 my-2">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-800">{mainItem.product.name} Subscription</h3>
                <p className="text-sm text-gray-600 mt-2">{mainItem.product.description}</p>
                {mainItem.product.marketingFeatures && mainItem.product.marketingFeatures.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-black">What’s included</h4>
                    <div className="grid grid-cols-1 gap-2 mt-2">
                      {mainItem.product.marketingFeatures.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-yellow-500 rounded-full"></span>
                          <p className="text-sm text-gray-600">{feature.name}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex flex-col bg-gray-50 border rounded-lg p-6 w-full md:w-80 h-auto">
                <div>
                  <p className="text-sm text-gray-600">Billed Monthly</p>
                  {mainItem.unit_amount ? (
                  <p className="text-3xl font-bold text-gray-800 mt-2">
                    {mainItem.currency} {mainItem.unit_amount}
                  </p>) : (
                    <p className="text-3xl font-bold text-gray-800 mt-2">Free Tier</p>)}
                  <p className="text-sm text-gray-600 mt-2">
                    Next renewal on {new Date(mainSubscription.current_period_end * 1000).toLocaleDateString()}
                  </p>
                </div>
                <div className="mt-4">
                  <button
                    className="mt-2 px-4 py-2 text-black text-sm font-medium rounded-md hover:brightness-90"
                    style={{ backgroundColor: '#FEDD03' }}
                    onClick={openPricingTableModal}
                  >
                    Manage Subscription
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-600 mt-4">No main subscription found.</p>
          )}

          {/* Pricing Table Modal with Clickaway Listener */}
          {modalOpen && (
            <div
              className="fixed inset-0 flex items-center justify-center z-50 bg-gray-900 bg-opacity-50"
              onClick={() => setModalOpen(false)} // Clickaway: close when clicking the overlay
            >
              <div
                className="bg-white rounded-lg shadow-sm p-6 w-full max-w-6xl relative"
                style={{ 
                  backgroundImage: `url(${lightRoad})`, 
                  backgroundSize: 'cover', 
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
                onClick={(e) => e.stopPropagation()} // Prevent click inside modal from closing it
              >
                {/* X button in top right */}
                <button
                  onClick={() => {
                    setModalOpen(false);
                    setFrequency(frequencies[0]);
                  }}
                  className="absolute top-4 right-4 text-gray-700 hover:text-gray-900"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
                <div className="mx-auto">
                  <div className="flex justify-center">
                    <fieldset aria-label="Payment frequency">
                      <RadioGroup
                        value={frequency}
                        onChange={setFrequency}
                        className="flex gap-x-2 rounded-full p-1 text-center text-xs/5 font-semibold ring-1 ring-inset ring-gray-200 bg-white shadow-md"
                      >
                        {frequencies.map((option) => (
                          <div key={option.value} className="flex items-center gap-2">
                            <Radio
                              value={option}
                              className="cursor-pointer rounded-full px-2 py-1 text-gray-500 data-[checked]:bg-yellow-600 data-[checked]:text-white"
                            >
                              {option.label}
                              {option.value === 'annually' && (
                                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-gray-700 bg-gray-200 opacity-70 rounded-full ml-1">
                                  Save 10%
                                </span>
                              )}
                            </Radio>
                          </div>
                        ))}
                      </RadioGroup>
                    </fieldset>
                  </div>
                  <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-4 lg:mx-0 lg:max-w-none lg:grid-cols-4">
                    {tiers.map((tier) => (
                      <div
                        key={tier.id}
                        className={classNames(
                          tier.featured ? 'bg-gray-900 ring-gray-900' : 'bg-white ring-gray-200 shadow-lg',
                          mainSubscription?.items[0]?.product.name === tier.name ? 'ring-2 ring-yellow-500' : '',
                          'rounded-lg p-6 ring-1 xl:p-8'
                        )}
                      >
                        <div className="flex items-center justify-between gap-x-4">
                          <h3
                            id={tier.id}
                            className={classNames(tier.featured ? 'text-white' : 'text-gray-900', 'text-lg font-semibold text-start')}
                          >
                            {tier.name}
                          </h3>
                          {mainSubscription?.items[0]?.product.name === tier.name && (
                            <p className="rounded-full bg-yellow-600/10 px-2.5 py-1 text-xs/5 font-semibold text-yellow-600">Current Plan</p>
                          )}
                        </div>
                        <p className={classNames(tier.featured ? 'text-gray-300' : 'text-gray-600', 'mt-2 text-sm text-start')}>
                          {tier.description}
                        </p>
                        {typeof tier.price !== 'string' ? (
                          <div className="mt-4">
                            {(() => {
                              const monthlyNum = parseInt(tier.price.monthly.replace('$', '').replace(',', ''));
                              const annualNum = parseInt(tier.price.annually.replace('$', '').replace(',', ''));
                              const saveAmount = monthlyNum * 12 - annualNum;
                              const monthlyEquivalent = (annualNum / 12).toFixed(0);
                              if (frequency.value === 'monthly') {
                                return (
                                  <>
                                    <div className="flex items-center justify-start gap-x-1 mb-2">
                                      <span
                                        className={classNames(
                                          tier.featured ? 'text-white' : 'text-gray-900',
                                          'text-2xl font-semibold tracking-tight'
                                        )}
                                      >
                                        {tier.price.monthly}
                                      </span>
                                      <span
                                        className={classNames(
                                          tier.featured ? 'text-gray-300' : 'text-gray-600',
                                          'text-sm font-semibold'
                                        )}
                                      >
                                        /month
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-start">
                                      <span className="text-sm font-medium text-green-600">
                                        Save ${saveAmount}/year with annual
                                      </span>
                                    </div>
                                  </>
                                );
                              } else {
                                return (
                                  <>
                                    <div className="flex items-center justify-start gap-x-1 mb-2">
                                      <span
                                        className={classNames(
                                          tier.featured ? 'text-white' : 'text-gray-900',
                                          'text-2xl font-semibold tracking-tight'
                                        )}
                                      >
                                        {tier.price.annually}
                                      </span>
                                      <span
                                        className={classNames(
                                          tier.featured ? 'text-gray-300' : 'text-gray-600',
                                          'text-sm font-semibold'
                                        )}
                                      >
                                        /year
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-start">
                                      <span className="text-sm font-medium text-green-600">
                                        ${monthlyEquivalent}/month billed annually
                                      </span>
                                    </div>
                                  </>
                                );
                              }
                            })()}
                          </div>
                        ) : (
                          <p className="mt-4 flex items-center justify-center gap-x-1">
                            ...
                          </p>
                        )}
                        <button
                          onClick={mainSubscription?.items[0]?.product.name === tier.name ? null : handleManageSubscription}
                          aria-describedby={tier.id}
                          disabled={mainSubscription?.items[0]?.product.name === tier.name}
                          className={classNames(
                            mainSubscription?.items[0]?.product.name === tier.name
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : tier.featured
                              ? 'bg-yellow-500 text-black hover:bg-yellow-600 focus-visible:outline-yellow-600'
                              : 'bg-yellow-500 text-black shadow-sm hover:bg-yellow-600 focus-visible:outline-yellow-500',
                            'mt-4 block w-full rounded-md px-3 py-2 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'
                          )}
                        >
                          {mainSubscription?.items[0]?.product.name === tier.name ? 'Current Plan' : tier.cta}
                        </button>
                        <ul
                          role="list"
                          className={classNames(
                            tier.featured ? 'text-gray-300' : 'text-gray-600',
                            'mt-4 space-y-2 text-sm text-left'
                          )}
                        >
                          {tier.features.map((feature) => (
                            <li key={feature} className="flex items-center justify-start gap-x-2">
                              <CheckIcon
                                aria-hidden="true"
                                className={classNames(tier.featured ? 'text-white' : 'text-yellow-600', 'h-5 w-5 flex-none')}
                              />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}